import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { KIKI } from "@/constants/testIds";
import { TESTIMONIAL_MEDIA } from "@/constants/testimonialMedia";
import { useT, useLang } from "@/lib/i18n";

// Employer chip — small rounded panel with favicon + short name.
// Falls back to a bold coloured badge (initials) if the favicon fails or is absent.
function EmployerChip({ employer, isHi }) {
  const [broken, setBroken] = useState(!employer?.logo);
  useEffect(() => setBroken(!employer?.logo), [employer?.logo]);
  if (!employer) return null;
  return (
    <div
      data-testid="testimonial-employer-chip"
      className="inline-flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 rounded-full border border-border bg-background/70 backdrop-blur-sm shadow-sm"
    >
      {!broken && employer.logo ? (
        <span className="w-7 h-7 grid place-items-center rounded-full bg-white shrink-0 border border-border overflow-hidden">
          <img
            src={employer.logo}
            alt={`${employer.short} logo`}
            className="w-5 h-5 object-contain"
            onError={() => setBroken(true)}
            draggable={false}
          />
        </span>
      ) : (
        <span
          className="w-7 h-7 grid place-items-center rounded-full text-[10px] font-black tracking-wider shrink-0"
          style={{ background: employer.badgeBg, color: employer.badgeInk }}
        >
          {employer.badge}
        </span>
      )}
      <span className={`text-[12px] font-semibold whitespace-nowrap ${isHi ? "font-hindi" : ""}`}>
        {employer.short}
      </span>
    </div>
  );
}

// Stylised gradient portrait — designer-illustrated stand-in for the student.
// If `media.photo` is set, renders the real headshot instead.
function Portrait({ media, name }) {
  const [broken, setBroken] = useState(!media?.photo);
  useEffect(() => setBroken(!media?.photo), [media?.photo]);
  const usePhoto = !broken && media?.photo;
  return (
    <div
      data-testid="testimonial-portrait"
      className="relative w-24 h-24 md:w-28 md:h-28 shrink-0 rounded-full overflow-hidden border-[3px] border-[#FFCC00] shadow-[0_18px_36px_-16px_rgba(0,0,0,0.35)]"
      style={usePhoto ? undefined : { background: media?.gradient || "#0A0A0A" }}
      aria-label={name}
    >
      {usePhoto ? (
        <img
          src={media.photo}
          alt={name}
          onError={() => setBroken(true)}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <span
          className="absolute inset-0 grid place-items-center font-black text-[30px] md:text-[34px] leading-none tracking-tight select-none"
          style={{ color: media?.ink || "#0A0A0A" }}
        >
          {media?.initials || "•"}
        </span>
      )}
      {/* Subtle noise/glow layer for depth */}
      <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-white/10" />
    </div>
  );
}

export function Testimonials() {
  const [idx, setIdx] = useState(0);
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";
  const QUOTES = t("testimonials.quotes");
  const q = QUOTES[idx] || QUOTES[0];
  const media = TESTIMONIAL_MEDIA[idx] || TESTIMONIAL_MEDIA[0];

  useEffect(() => {
    if (idx >= QUOTES.length) setIdx(0);
  }, [QUOTES.length, idx]);

  useEffect(() => {
    const timer = setInterval(() => setIdx((i) => (i + 1) % QUOTES.length), 9000);
    return () => clearInterval(timer);
  }, [QUOTES.length]);

  const stipend = t("testimonials.stipendSalary");

  return (
    <section data-testid={KIKI.testimonials} className="relative py-16 md:py-24 bg-secondary/50">
      <div className="max-w-[90rem] mx-auto px-6 md:px-10">
        <div className="flex items-baseline gap-4 mb-8 md:mb-10">
          <span className="font-mono text-sm text-muted-foreground">07</span>
          <span className={`label-overline text-muted-foreground ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("testimonials.overline")}</span>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
          <div className="col-span-12 md:col-span-7">
            <Quote className="w-10 h-10 md:w-12 md:h-12 text-[#FFCC00] mb-5 md:mb-6" strokeWidth={1.4} />

            {/* Portrait + employer chip band */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`p-${idx}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-5 mb-7"
              >
                <Portrait media={media} name={q.name} />
                <EmployerChip employer={media.employer} isHi={isHi} />
              </motion.div>
            </AnimatePresence>

            <AnimatePresence mode="wait">
              <motion.blockquote
                key={`q-${lang}-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className={`italic font-normal text-2xl md:text-3xl lg:text-[38px] leading-[1.22] tracking-tight max-w-3xl ${isHi ? "font-hindi not-italic" : "font-display"}`}
              >
                &ldquo;{q.q}&rdquo;
              </motion.blockquote>
            </AnimatePresence>

            <div className="mt-8 md:mt-10 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className={`font-bold text-lg leading-none truncate ${isHi ? "font-hindi" : "font-sans"}`}>{q.name}</div>
                <div className={`label-overline text-muted-foreground mt-2 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{q.role} · {q.company}</div>
                <div className={`label-overline text-muted-foreground text-[10px] mt-1 ${isHi ? "font-hindi tracking-normal text-[11px]" : ""}`}>{q.origin}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  data-testid="testimonial-prev"
                  aria-label="Previous"
                  onClick={() => setIdx((i) => (i - 1 + QUOTES.length) % QUOTES.length)}
                  className="w-11 h-11 grid place-items-center rounded-full border border-border hover:border-foreground hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" strokeWidth={1.8} />
                </button>
                <button
                  data-testid="testimonial-next"
                  aria-label="Next"
                  onClick={() => setIdx((i) => (i + 1) % QUOTES.length)}
                  className="w-11 h-11 grid place-items-center rounded-full border border-border hover:border-foreground hover:bg-muted transition-colors"
                >
                  <ChevronRight className="w-4 h-4" strokeWidth={1.8} />
                </button>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-5">
            <div className="brochure-card bg-card border border-border p-8 md:p-10 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-[#FFCC00]/20 rounded-full blur-3xl" />
              <div className={`label-overline text-muted-foreground mb-3 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("testimonials.fromDay1")}</div>
              <div className={`font-black text-4xl md:text-5xl leading-[1.05] tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}>{stipend.a}<br />{stipend.b}<br />{stipend.c}</div>
              <p className={`mt-6 text-sm text-muted-foreground leading-relaxed max-w-xs ${isHi ? "font-hindi" : ""}`}>
                {t("testimonials.stipendCopy")}
              </p>

              {/* Mini-thumbnail rail — visual index of all quotes */}
              <div className="mt-8 pt-5 border-t border-border">
                <div className="flex items-center gap-2 flex-wrap">
                  {QUOTES.map((qq, i) => {
                    const m = TESTIMONIAL_MEDIA[i];
                    const active = i === idx;
                    return (
                      <button
                        key={i}
                        onClick={() => setIdx(i)}
                        aria-label={`Testimonial ${i + 1}: ${qq.name}`}
                        className={`relative w-11 h-11 rounded-full overflow-hidden shrink-0 transition-all duration-500 ${active ? "ring-2 ring-offset-2 ring-offset-card ring-[#FFCC00] scale-105" : "opacity-55 hover:opacity-100"}`}
                        style={m?.photo ? undefined : { background: m?.gradient || "#0A0A0A" }}
                      >
                        {m?.photo ? (
                          <img src={m.photo} alt={qq.name} className="w-full h-full object-cover" draggable={false} />
                        ) : (
                          <span
                            className="absolute inset-0 grid place-items-center font-black text-[12px] tracking-tight"
                            style={{ color: m?.ink || "#0A0A0A" }}
                          >
                            {m?.initials}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
